import { useCallback, useMemo, useReducer, useRef } from 'react';

/**
 * Gestion d'erreur des tuiles de fond de carte.
 *
 * Un fond qui ne répond pas (panne du fournisseur, clé refusée, plafond d'usage
 * atteint, réseau coupé sans copie gardée) laissait jusqu'ici des carrés gris
 * sans un mot. On compte les tuiles perdues et réussies depuis le dernier
 * chargement de la vue: assez de pertes, et la carte le dit, avec deux issues
 * (réessayer, ou passer au plan standard).
 */

/** Nombre de tuiles perdues, sans réussite équivalente, à partir duquel on prévient. */
export const SEUIL_TUILES_PERDUES = 3;

/** Le fond de repli, toujours disponible dans `MAP_LAYERS`. */
export const FOND_DE_REPLI = 'streets';

/**
 * Hors ligne, sans copie gardée, le service worker rend une tuile transparente
 * de 1 pixel (voir public/service-worker.js) plutôt qu'une image cassée. Elle
 * se charge « avec succès » mais n'est pas une vraie tuile: on la compte comme
 * perdue. Les vraies tuiles font 256 ou 512 pixels.
 */
export const estTuileVide = (tuile) =>
  Boolean(tuile) && Number(tuile.naturalWidth) > 0 && Number(tuile.naturalWidth) < 8;

export const etatInitialTuiles = { erreurs: 0, reussies: 0, essai: 0 };

/** Vrai quand assez de tuiles manquent pour que ce ne soit plus un détail. */
export const fondIndisponible = ({ erreurs, reussies }) =>
  erreurs >= SEUIL_TUILES_PERDUES && erreurs >= reussies;

/**
 * - `perdue`   une tuile n'a pas pu être chargée;
 * - `chargee`  une tuile s'est affichée;
 * - `vue`      Leaflet commence à charger une nouvelle vue: on repart de zéro;
 * - `reessai`  le bouton « Réessayer »: compteurs remis à zéro, `essai` change
 *              pour que la couche de tuiles soit recréée;
 * - `reinit`   autre fond choisi.
 */
export function suiviTuiles(etat, action) {
  switch (action.type) {
    case 'perdue':
      return { ...etat, erreurs: etat.erreurs + 1 };
    case 'chargee':
      return { ...etat, reussies: etat.reussies + 1 };
    case 'vue':
      return etat.erreurs === 0 && etat.reussies === 0
        ? etat
        : { ...etat, erreurs: 0, reussies: 0 };
    case 'reessai':
      return { erreurs: 0, reussies: 0, essai: etat.essai + 1 };
    case 'reinit':
      return { ...etatInitialTuiles, essai: etat.essai };
    default:
      return etat;
  }
}

/**
 * Crochet de la carte: les gestionnaires à poser sur la couche de tuiles, et
 * l'état à afficher. `reessayer` recrée la couche (clé différente).
 */
export function useSuiviTuiles() {
  const [etat, envoyer] = useReducer(suiviTuiles, etatInitialTuiles);
  const dernier = useRef(etat);
  dernier.current = etat;

  const gestionnaires = useMemo(
    () => ({
      tileerror: () => envoyer({ type: 'perdue' }),
      tileload: (evenement) =>
        envoyer({ type: estTuileVide(evenement?.tile) ? 'perdue' : 'chargee' }),
      loading: () => envoyer({ type: 'vue' }),
    }),
    [],
  );
  const reessayer = useCallback(() => envoyer({ type: 'reessai' }), []);
  const reinitialiser = useCallback(() => envoyer({ type: 'reinit' }), []);

  return {
    indisponible: fondIndisponible(etat),
    essai: etat.essai,
    gestionnaires,
    reessayer,
    reinitialiser,
  };
}
